import { useState } from 'react';
import { RefreshCw, Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Textarea } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function RandomGenerator() {
  return (
    <div>
      <ToolHeader
        toolId="random-generator"
        title="Random generator"
        description="Numbers, picks, dice rolls, and coin flips."
      />
      <Tabs defaultValue="number">
        <TabsList className="mb-4">
          <TabsTrigger value="number">Number</TabsTrigger>
          <TabsTrigger value="pick">Pick</TabsTrigger>
          <TabsTrigger value="dice">Dice</TabsTrigger>
          <TabsTrigger value="coin">Coin</TabsTrigger>
        </TabsList>
        <TabsContent value="number"><NumberPanel /></TabsContent>
        <TabsContent value="pick"><PickPanel /></TabsContent>
        <TabsContent value="dice"><DicePanel /></TabsContent>
        <TabsContent value="coin"><CoinPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function NumberPanel() {
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [count, setCount] = useState(1);
  const [unique, setUnique] = useState(false);
  const [list, setList] = useState<number[]>([42]);

  function go() {
    if (max < min) return;
    if (unique && count > max - min + 1) return;
    const out: number[] = [];
    const used = new Set<number>();
    for (let i = 0; i < count; i++) {
      let n = randInt(min, max);
      if (unique) {
        while (used.has(n)) n = randInt(min, max);
        used.add(n);
      }
      out.push(n);
    }
    setList(out);
  }

  return (
    <Card>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Field label="Min">
          <Input type="number" value={min} onChange={(e) => setMin(Number(e.target.value))} />
        </Field>
        <Field label="Max">
          <Input type="number" value={max} onChange={(e) => setMax(Number(e.target.value))} />
        </Field>
        <Field label="Count">
          <Input
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(500, Number(e.target.value))))}
          />
        </Field>
        <Field label="Unique">
          <select
            value={unique ? 'y' : 'n'}
            onChange={(e) => setUnique(e.target.value === 'y')}
            className="w-full h-9 px-3 text-sm rounded-lg bg-bg-elev border border-border"
          >
            <option value="n">No</option>
            <option value="y">Yes</option>
          </select>
        </Field>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="primary" onClick={go}>
          <RefreshCw size={12} />
          Generate
        </Button>
        <Button
          variant="ghost"
          onClick={async () => {
            const ok = await copyToClipboard(list.join(', '));
            if (ok) toast.success('Copied');
          }}
        >
          <Copy size={12} />
          Copy
        </Button>
      </div>
      <div className="font-mono text-2xl break-all">{list.join(', ')}</div>
    </Card>
  );
}

function PickPanel() {
  const [items, setItems] = useState('Mumbai\nBengaluru\nDelhi\nPune\nGoa');
  const [pick, setPick] = useState('');
  function go() {
    const arr = items.split('\n').filter(Boolean);
    if (!arr.length) return;
    setPick(arr[Math.floor(Math.random() * arr.length)]);
  }
  return (
    <Card>
      <Field label="Items (one per line)">
        <Textarea
          value={items}
          onChange={(e) => setItems(e.target.value)}
          className="min-h-[160px]"
        />
      </Field>
      <div className="mt-3 flex items-center justify-between">
        <Button variant="primary" onClick={go}>
          <RefreshCw size={12} />
          Pick one
        </Button>
        {pick && <div className="text-2xl font-semibold">→ {pick}</div>}
      </div>
    </Card>
  );
}

function DicePanel() {
  const [sides, setSides] = useState(6);
  const [count, setCount] = useState(1);
  const [rolls, setRolls] = useState<number[]>([4]);
  function go() {
    setRolls(Array.from({ length: count }, () => randInt(1, sides)));
  }
  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Sides">
          <Input type="number" value={sides} onChange={(e) => setSides(Math.max(2, Number(e.target.value)))} />
        </Field>
        <Field label="Dice count">
          <Input type="number" value={count} onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value))))} />
        </Field>
      </div>
      <Button variant="primary" onClick={go}>
        <RefreshCw size={12} />
        Roll
      </Button>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {rolls.map((r, i) => (
          <div
            key={i}
            className="size-14 rounded-xl bg-bg-inset border border-border flex items-center justify-center text-2xl font-semibold"
          >
            {r}
          </div>
        ))}
        {rolls.length > 1 && (
          <div className="ml-2 text-sm text-fg-muted">
            sum: <span className="font-mono font-semibold">{rolls.reduce((a, b) => a + b, 0)}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

function CoinPanel() {
  const [result, setResult] = useState<'Heads' | 'Tails' | null>(null);
  return (
    <Card>
      <div className="flex flex-col items-center justify-center py-8">
        <div className="text-7xl mb-4">{result === 'Heads' ? 'H' : result === 'Tails' ? 'T' : '·'}</div>
        <div className="text-xl font-semibold mb-4">{result ?? 'Flip the coin'}</div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => setResult(Math.random() < 0.5 ? 'Heads' : 'Tails')}
        >
          <RefreshCw size={14} />
          Flip
        </Button>
      </div>
    </Card>
  );
}

export default RandomGenerator;
